import { FC } from 'react';
import { InterventionReportProps } from './interventionReports.type';
import { InterventionReportsTableHeader } from './interventionReportsTableHeader';
import { InterventionReportsTableFilter } from './interventionReportsTableFilter';
import Table from '../../component/table/table';
import {
  getInterventionReportsKebabOptions,
  getInterventionReportsTableHeader,
  getInterventionReportTitle,
  formatSelectedReportRefs,
} from './interventionReportsHelper';
import InterventionReportForm from '../../component/interventionReportForm/interventionReportForm';
import { useInterventionReports } from '../../hooks/useInterventionReports';
import { getConsecutiveSweeps } from '../../utils/helper';
import { InterventionReportDetails } from './interventionReportDetails';
import UserVerificationModal from '../../component/userVerificationModal/userVerificationModal';
import { Trans } from 'react-i18next';
import { ConfirmationModalV2 } from '../../component/confirmationModalV2/confirmationModalV2';
import SelectPlayerModal from '../../component/selectPlayerModal/selectPlayerModal';

export const InterventionReports: FC<InterventionReportProps> = () => {
  const {
    InterventionReports,
    handleClearFilters,
    handleReportInterventionClose,
    handleKebabActionFromKebabMenu,
    handleOnChangeSearch,
    handleDateApply,
    loading,
    isTablet,
    organizationContextLoading,
    selectedVenueBranch,
    filters,
    openFilterModal,
    toggleFilterModal,
    handleApplyFilters,
    openCreateIntervention,
    isReviewAndEditMode,
    selectedRows,
    handleSelectionChange,
    handleRowClick,
    interventionReportForCreate,
    t,
    selectedInterventionReport,
    handleReviewAndSignClick,
    closeInterventionReportDetails,
    setSelectedRows,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    handleDeleteInterventionReports,
    handleDeleteClick,
    showUserVerification,
    handleOnVerify,
    setShowUserVerification,
    handleReportInterventionClick,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    handleOnProceed,
    verifiedUser,
  } = useInterventionReports();

  return (
    <div className="header-content">
      <div
        className="table-content pt-6 flex flex-col overflow-hidden"
        style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
      >
        {showUserVerification && (
          <UserVerificationModal
            show={showUserVerification}
            onToggle={() =>
              setShowUserVerification((currentValue: boolean) => !currentValue)
            }
            onVerified={handleOnVerify}
            scope={'intc'}
          />
        )}
        {showSelectPlayerModal && (
          <SelectPlayerModal
            onClose={() => {
              setShowSelectPlayerModal(false);
            }}
            onProceed={handleOnProceed}
          />
        )}
        <InterventionReportsTableHeader
          reportLabel={
            selectedRows.length > 1
              ? t('common.multi_report')
              : t('sweeps.report_one')
          }
          searchQuery={filters.searchQuery}
          handleOnChangeSearch={handleOnChangeSearch}
          selectedCount={selectedRows.length}
          selectedDate={filters.selectedDate}
          disableSettingMenu={true}
          handleReportInterventionClick={handleReportInterventionClick}
          handleSelectedDate={handleDateApply}
          handleClose={() => setSelectedRows([])}
          handleDelete={handleDeleteClick}
          handleDownload={() => {
            // TODO: Implement download functionality
            console.log('Download selected reports:', selectedRows);
          }}
          showDownloadLoading={false}
        >
          <InterventionReportsTableFilter
            isOpenUserModal={openFilterModal}
            selectedBranchIDs={
              selectedVenueBranch ? [selectedVenueBranch._id] : []
            }
            handleClearFilters={handleClearFilters}
            handleApplyFilters={handleApplyFilters}
            toggleFilterModal={toggleFilterModal}
          />
        </InterventionReportsTableHeader>
        {/* Table    */}
        <div className="flex-grow overflow-y-auto max-h-[700px]">
          <Table
            onSelectionChange={handleSelectionChange}
            tableHeader={getInterventionReportsTableHeader(isTablet, t)}
            selectedRows={selectedRows}
            tableData={InterventionReports}
            loading={loading || organizationContextLoading}
            onViewDetail={handleRowClick}
            onKebabSelect={handleKebabActionFromKebabMenu}
            loadingMenu={{ rowIndex: -1, action: '' }}
            getKebabMenuOptions={getInterventionReportsKebabOptions as any}
          />
        </div>
        {openCreateIntervention && (
          <InterventionReportForm
            title={getInterventionReportTitle(
              interventionReportForCreate?.reportRefNo,
              isReviewAndEditMode,
              t,
            )}
            data={interventionReportForCreate}
            player={null}
            consecutiveSweeps={getConsecutiveSweeps(
              interventionReportForCreate,
            )}
            harmCount={null}
            interventionOffered={null}
            interventionRequested={null}
            isReviewAndEditMode={isReviewAndEditMode}
            verifiedUser={verifiedUser}
            onBack={handleReportInterventionClose}
          />
        )}
        {selectedInterventionReport && (
          <InterventionReportDetails
            interventionReport={selectedInterventionReport}
            onClose={closeInterventionReportDetails}
            onReviewAndSign={handleReviewAndSignClick}
          />
        )}
        {showDeleteConfirmation && (
          <ConfirmationModalV2
            note="interventionReports.deletionNote"
            isOpen={showDeleteConfirmation}
            showConfirmButton
            title="common.deleteReport"
            onClose={() => setShowDeleteConfirmation(false)}
            message={
              <Trans
                i18nKey="interventionReports.deleteConfirmation"
                values={{
                  value: formatSelectedReportRefs(selectedRows),
                  report:
                    selectedRows.length === 1
                      ? t('interventionReports.singleReport')
                      : t('interventionReports.multipleReports'),
                }}
                components={{ bold: <strong /> }}
              />
            }
            isLoading={deleting}
            onConfirm={handleDeleteInterventionReports}
          />
        )}
      </div>
    </div>
  );
};
